// The visitor's demo window, read or started on the real api (spec/24 §3), and counted down on its clock.
//
//   start: true    `POST /v1/me/demo` — the console and the kiosk start the window when they open
//   start: false   `GET /v1/me/demo`  — the landing page only reads it
//
// A `429 demo-limit-reached` is not an error to the visitor: it is "your demo has ended", so the window is read
// back with `GET` and shown as ended, with when the next can start.

import type { ApiClient } from '@clavian/api-client';
import { demoClockFrom, demoWindowStatus, type DemoClock, type DemoWindowStatus, type DemoWindowWire } from '@clavian/demo';
import { useCallback, useEffect, useState } from 'react';

import { problemCode } from '../api/problems';

export type DemoWindowState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'error' }
  | { readonly kind: 'ready'; readonly window: DemoWindowWire | null; readonly clock: DemoClock };

/** Read (or start) the window once, and again on `reload()`. */
export function useDemoWindow(api: ApiClient, options: { readonly start: boolean; readonly enabled?: boolean }): {
  readonly state: DemoWindowState;
  readonly reload: (start?: boolean) => void;
} {
  const [state, setState] = useState<DemoWindowState>({ kind: 'loading' });
  const [request, setRequest] = useState({ serial: 0, start: options.start });
  const enabled = options.enabled ?? true;

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    setState({ kind: 'loading' });
    const load = async (): Promise<DemoWindowState> => {
      try {
        const access = request.start ? await api.startMyDemo() : await api.getMyDemo();
        return { kind: 'ready', window: access.window, clock: demoClockFrom(access, Date.now()) };
      } catch (error) {
        if (problemCode(error) !== 'demo_limit_reached') throw error;
        const access = await api.getMyDemo();
        return { kind: 'ready', window: access.window, clock: demoClockFrom(access, Date.now()) };
      }
    };
    load().then(
      (next) => {
        if (!cancelled) setState(next);
      },
      () => {
        if (!cancelled) setState({ kind: 'error' });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [api, request, enabled]);

  const reload = useCallback((start?: boolean) => setRequest((current) => ({ serial: current.serial + 1, start: start ?? current.start })), []);
  return { state, reload };
}

/** The device's clock, re-read every `intervalMs`: the banner's countdown ticks with it. */
export function useTick(intervalMs = 1_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);
  return now;
}

/** Where the window stands now, on the server's clock. */
export function useDemoWindowStatus(state: DemoWindowState): DemoWindowStatus | null {
  const now = useTick();
  return state.kind === 'ready' ? demoWindowStatus(state.window, state.clock, now) : null;
}
